import React, { useMemo, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Search, Sparkles, Target, Trophy, ChevronRight } from 'lucide-react';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { useAppStore } from '../app/hooks/useAppStore';
import { MODULES, type ModuleId, type ModuleData, getFamiliesForModule, type FamilyDefinition, getAllowedTiersForGroup, getModuleIcon, getModuleColors } from '../content';
import type { FamilyMasteryState } from '../mastery';
import { getFamilyMasteryKey } from '../mastery';
import { DjayAvatar, useDjaySelection } from '../mascot';
import { getMasteryLabelInfo } from './shared/masteryLabelInfo';
import { MasteryRing } from '../portals/shared/MasteryRing';
// ─── Types ────────────────────────────────────────────────────
export type FamilyPickerMode = 'sprint' | 'challenge' | 'free';
interface FamilyPickerProps {
  onClose: () => void;
  onSelect: (familyId: string) => void;
  mode: FamilyPickerMode;
}
interface FamilyWithMastery {
  family: FamilyDefinition;
  mastery: FamilyMasteryState | null;
  masteryScore: number;
  attempts: number;
}
// ─── Mode Config ──────────────────────────────────────────────
const MODE_CONFIG = {
  sprint: {
    gradient: 'from-indigo-500 via-blue-500 to-cyan-400',
    accent: 'indigo',
    message: 'Kies een onderwerp om te trainen! 🎯',
    emotion: 'pointing' as const,
    title: 'Focus Sprint'
  },
  free: {
    gradient: 'from-emerald-500 via-teal-500 to-cyan-400',
    accent: 'teal',
    message: 'Kies wat je wilt oefenen — helemaal vrij! 🎲',
    emotion: 'happy' as const,
    title: 'Vrij Oefenen'
  },
  challenge: {
    gradient: 'from-violet-500 via-purple-500 to-fuchsia-400',
    accent: 'violet',
    message: 'Kies je uitdaging! Dit wordt spannend 💪',
    emotion: 'excited' as const,
    title: 'Uitdaging'
  }
} as const;
// ─── Main Component ───────────────────────────────────────────
export function FamilyPicker({
  onClose,
  onSelect,
  mode
}: FamilyPickerProps) {
  const {
    state,
    selectedGroup
  } = useAppStore();
  const {
    selectedVariant
  } = useDjaySelection();
  const familyMastery = state.familyMastery || {};
  // Step 1: null, Step 2: ModuleId
  const [selectedModule, setSelectedModule] = useState<ModuleId | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const config = MODE_CONFIG[mode];
  const allowedTiers = useMemo(() => getAllowedTiersForGroup(selectedGroup), [selectedGroup]);
  // Get all families with mastery data, filtered and sorted by mode
  const allFamiliesWithMastery = useMemo(() => {
    const result: FamilyWithMastery[] = [];
    for (const module of MODULES) {
      const families = getFamiliesForModule(module.id);
      for (const family of families) {
        if (mode !== 'free' && !allowedTiers.has(family.tier)) continue;
        const masteryKey = getFamilyMasteryKey(family.familyId, selectedGroup);
        const mastery = familyMastery[masteryKey] || null;
        const masteryScore = mastery?.masteryScore ?? 0;
        const attempts = mastery?.attempts ?? 0;
        if (mode === 'challenge' && masteryScore < 70) continue;
        if (mode === 'sprint' && attempts === 0) continue;
        result.push({
          family,
          mastery,
          masteryScore,
          attempts
        });
      }
    }
    if (mode === 'sprint') result.sort((a, b) => a.masteryScore - b.masteryScore);else if (mode === 'challenge') result.sort((a, b) => b.masteryScore - a.masteryScore);else result.sort((a, b) => a.family.description.localeCompare(b.family.description));
    return result;
  }, [selectedGroup, familyMastery, allowedTiers, mode]);
  // Calculate module aggregates for Step 1
  const moduleAggregates = useMemo(() => {
    return MODULES.map(module => {
      const moduleFamilies = allFamiliesWithMastery.filter(f => f.family.module === module.id);
      const count = moduleFamilies.length;
      const avgMastery = count > 0 ? moduleFamilies.reduce((sum, f) => sum + f.masteryScore, 0) / count : 0;
      return {
        module,
        count,
        avgMastery
      };
    }).filter(m => m.count > 0); // Only show modules that have available families for this mode
  }, [allFamiliesWithMastery]);
  // Get families for selected module (Step 2)
  const selectedModuleFamilies = useMemo(() => {
    if (!selectedModule) return [];
    return allFamiliesWithMastery.filter(f => f.family.module === selectedModule);
  }, [allFamiliesWithMastery, selectedModule]);
  // Filter by search query (Step 2)
  const filteredFamilies = useMemo(() => {
    if (!searchQuery.trim()) return selectedModuleFamilies;
    const query = searchQuery.toLowerCase();
    return selectedModuleFamilies.filter(({
      family
    }) => family.familyId.toLowerCase().includes(query) || family.description.toLowerCase().includes(query) || family.skill.toLowerCase().includes(query) || family.subSkill.toLowerCase().includes(query));
  }, [selectedModuleFamilies, searchQuery]);
  const hasPracticedFamilies = useMemo(() => Object.values(familyMastery).some(m => m.attempts > 0), [familyMastery]);
  const hasChallengeReadyFamilies = useMemo(() => Object.values(familyMastery).some(m => m.masteryScore >= 70), [familyMastery]);
  const djaysPick = allFamiliesWithMastery.length > 0 ? allFamiliesWithMastery[0] : null;
  const showDjayPickInModule = !searchQuery && selectedModule && filteredFamilies.length > 0 && djaysPick && filteredFamilies[0].family.familyId === djaysPick.family.familyId;
  return <div className="bg-slate-50 min-h-screen pb-8" data-id="element-2380">
      {/* ── Colorful Header ── */}
      <div className={`bg-gradient-to-br ${config.gradient} relative overflow-hidden`} data-id="element-2381">
        <div className="absolute top-0 right-0 w-48 h-48 bg-white/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/4" data-id="element-2382" />
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/10 rounded-full blur-2xl translate-y-1/4 -translate-x-1/4" data-id="element-2383" />

        <div className="relative z-10 px-6 sm:px-8 md:px-10 pt-6 md:pt-10 pb-6 max-w-4xl mx-auto" data-id="element-2384">
          <button onClick={selectedModule ? () => {
          setSelectedModule(null);
          setSearchQuery('');
        } : onClose} className="flex items-center gap-2 text-white/80 hover:text-white font-bold mb-4 transition-colors text-sm" data-id="element-2385">
            <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center" data-id="element-2386">
              <ArrowLeft className="w-4 h-4" data-id="element-2387" />
            </div>
            {selectedModule ? 'Terug naar vakken' : 'Terug naar Oefenlab'}
          </button>

          <div className="flex items-center gap-4" data-id="element-2388">
            <motion.div className="flex-shrink-0" animate={{
            y: [0, -4, 0]
          }} transition={{
            duration: 2.5,
            repeat: Infinity,
            ease: 'easeInOut'
          }} data-id="element-2389">
              <DjayAvatar size="lg" emotion={config.emotion} variant={selectedVariant} animate data-id="element-2390" />
            </motion.div>

            <div className="flex-1 min-w-0" data-id="element-2391">
              <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight leading-tight flex items-center gap-3" data-id="element-2392">
                {config.title}
                {mode === 'free' && <span className="text-[10px] font-black bg-emerald-100 text-emerald-700 px-2 py-1 rounded-lg uppercase tracking-wider" data-id="element-2393">
                    Experimenteel
                  </span>}
              </h1>
              <div className="mt-2 bg-white/20 backdrop-blur-sm rounded-2xl px-3 py-2 inline-block max-w-full" data-id="element-2394">
                <p className="text-white text-sm font-bold truncate" data-id="element-2395">
                  {selectedModule ? `Kies een onderwerp voor ${MODULES.find(m => m.id === selectedModule)?.title}` : config.message}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 sm:px-8 md:px-10 pt-6" data-id="element-2396">
        <AnimatePresence mode="wait" data-id="element-2397">
          {!selectedModule /* ── Step 1: Module Selection ── */ ? <motion.div key="step1" initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="space-y-6" data-id="element-2398">
              {allFamiliesWithMastery.length === 0 ? <EmptyState mode={mode} hasPracticedFamilies={hasPracticedFamilies} hasChallengeReadyFamilies={hasChallengeReadyFamilies} selectedVariant={selectedVariant} data-id="element-2399" /> : <>
                  {/* Djay Kiest Quick Start (F-2) */}
                  {djaysPick && mode !== 'free' && <DjayPickCard item={djaysPick} onSelect={onSelect} accentColor={config.accent} isQuickStart={true} data-id="element-2400" />}

                  <div data-id="element-2401">
                    <h2 className="text-lg font-black text-slate-800 mb-4" data-id="element-2402">
                      Kies een vak
                    </h2>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-2403">
                      {moduleAggregates.map(({
                  module,
                  count,
                  avgMastery
                }, index) => <ModuleCard key={module.id} module={module} count={count} avgMastery={avgMastery} index={index} onClick={() => setSelectedModule(module.id)} data-id="element-2404" />)}
                    </div>
                  </div>
                </>}
            </motion.div> /* ── Step 2: Family Selection ── */ : <motion.div key="step2" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: 20
        }} className="space-y-4" data-id="element-2405">
              <div className="sticky top-0 z-30 bg-slate-50/95 backdrop-blur-sm py-2 -mx-6 px-6 sm:mx-0 sm:px-0" data-id="element-2406">
                <Input type="text" placeholder="Zoek een onderwerp..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} icon={<Search className="w-4 h-4" data-id="element-2408" />} variant="student" size="sm" data-id="element-2407" />
              </div>

              <div className="flex items-center justify-between mb-2" data-id="element-2409">
                <p className="text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-2410">
                  {filteredFamilies.length} onderwerp
                  {filteredFamilies.length !== 1 ? 'en' : ''}
                </p>
              </div>

              {filteredFamilies.length === 0 ? <div className="text-center py-12" data-id="element-2411">
                  <p className="text-slate-500 font-medium" data-id="element-2412">
                    Geen onderwerpen gevonden voor deze zoekopdracht.
                  </p>
                </div> : <div className="space-y-2" data-id="element-2413">
                  {showDjayPickInModule && <DjayPickCard item={filteredFamilies[0]} onSelect={onSelect} accentColor={config.accent} isQuickStart={false} data-id="element-2414" />}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-2415">
                    {filteredFamilies.slice(showDjayPickInModule ? 1 : 0).map(({
                family,
                masteryScore,
                attempts
              }, index) => <FamilyCard key={family.familyId} family={family} masteryScore={masteryScore} attempts={attempts} index={index} onSelect={onSelect} data-id="element-2416" />)}
                  </div>
                </div>}
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}
// ─── Sub-components ───────────────────────────────────────────
function ModuleCard({
  module,
  count,
  avgMastery,
  index,
  onClick
}: {
  module: ModuleData;
  count: number;
  avgMastery: number;
  index: number;
  onClick: () => void;
}) {
  const ModIcon = getModuleIcon(module.icon);
  const colors = getModuleColors(module.color);
  const masteryInfo = getMasteryLabelInfo(avgMastery);
  return <motion.button initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: Math.min(index * 0.05, 0.3)
  }} onClick={onClick} className="w-full text-left p-4 rounded-3xl border-2 border-slate-200 bg-white hover:border-slate-300 hover:shadow-lg active:scale-[0.98] transition-all group relative overflow-hidden flex flex-col h-full" data-id="element-2417">
      <div className="flex items-start justify-between mb-3" data-id="element-2418">
        <div className={`w-12 h-12 rounded-2xl flex items-center justify-center ${colors.light}`} data-id="element-2419">
          <ModIcon className={`w-6 h-6 ${colors.text}`} data-id="element-2420" />
        </div>
        <div className="bg-slate-100 text-slate-500 text-[10px] font-bold px-2 py-1 rounded-lg" data-id="element-2421">
          {count} items
        </div>
      </div>

      <h3 className="text-base font-black text-slate-800 mb-1 group-hover:text-slate-900 transition-colors" data-id="element-2422">
        {module.title}
      </h3>

      <div className="mt-auto pt-3" data-id="element-2423">
        <div className="flex items-center justify-between mb-1.5" data-id="element-2424">
          <span className={`text-xs font-bold ${masteryInfo.color}`} data-id="element-2425">
            {masteryInfo.label}
          </span>
          <span className="text-[10px] font-bold text-slate-400" data-id="element-2426">
            {Math.round(avgMastery)}%
          </span>
        </div>
        <div className="w-full h-2 bg-slate-100 rounded-full overflow-hidden" data-id="element-2427">
          <motion.div className="h-full rounded-full" style={{
          backgroundColor: masteryInfo.ring
        }} initial={{
          width: 0
        }} animate={{
          width: `${Math.min(100, Math.max(0, avgMastery))}%`
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-2428" />
        </div>
      </div>
    </motion.button>;
}
function EmptyState({
  mode,
  hasPracticedFamilies,
  hasChallengeReadyFamilies,
  selectedVariant
}: {
  mode: FamilyPickerMode;
  hasPracticedFamilies: boolean;
  hasChallengeReadyFamilies: boolean;
  selectedVariant: string;
}) {
  const title = mode === 'sprint' && !hasPracticedFamilies ? 'Je moet eerst meer oefenen!' : mode === 'challenge' && !hasChallengeReadyFamilies ? 'Nog geen onderwerpen klaar' : 'Geen resultaten gevonden';
  const message = mode === 'sprint' && !hasPracticedFamilies ? 'Maak eerst wat oefeningen in een vak. Daarna kun je hier extra trainen!' : mode === 'challenge' && !hasChallengeReadyFamilies ? 'Oefen meer tot je minimaal 70% beheersing hebt.' : 'Probeer een andere zoekopdracht of selecteer een ander vak.';
  return <motion.div initial={{
    opacity: 0,
    scale: 0.95
  }} animate={{
    opacity: 1,
    scale: 1
  }} className="flex flex-col items-center justify-center py-16 text-center px-6" data-id="element-2429">
      <div className="mb-4" data-id="element-2430">
        <DjayAvatar size="xl" emotion="thinking" variant={selectedVariant} data-id="element-2431" />
      </div>
      <h3 className="text-xl font-black text-slate-800 mb-2" data-id="element-2432">{title}</h3>
      <p className="text-sm text-slate-500 max-w-sm font-medium leading-relaxed" data-id="element-2433">
        {message}
      </p>
    </motion.div>;
}
function FamilyCard({
  family,
  masteryScore,
  attempts,
  index,
  onSelect
}: {
  family: FamilyDefinition;
  masteryScore: number;
  attempts: number;
  index: number;
  onSelect: (id: string) => void;
}) {
  const masteryInfo = getMasteryLabelInfo(masteryScore);
  return <motion.button initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    delay: Math.min(index * 0.03, 0.3)
  }} onClick={() => onSelect(family.familyId)} className="w-full text-left p-3.5 rounded-2xl border-2 border-slate-200 bg-white hover:border-indigo-300 hover:shadow-lg active:scale-[0.98] transition-all group relative overflow-hidden flex flex-col h-full" data-id="element-2434">
      <div className="flex items-start justify-between mb-2" data-id="element-2435">
        <div className="flex-1 min-w-0 pr-2" data-id="element-2436">
          <h4 className="text-sm font-black text-slate-800 leading-tight line-clamp-2 group-hover:text-indigo-700 transition-colors" data-id="element-2437">
            {family.description}
          </h4>
          <div className="flex items-center gap-1.5 mt-1.5" data-id="element-2438">
            <Badge variant="neutral" size="sm" data-id="element-2439">
              {family.levelCode}
            </Badge>
            <span className="text-[10px] text-slate-400 font-medium truncate" data-id="element-2440">
              {family.skill}
            </span>
          </div>
        </div>
        <div className="flex-shrink-0" data-id="element-2441">
          <MasteryRing score={masteryScore} size={40} data-id="element-2442" />
        </div>
      </div>

      <div className="mt-auto pt-2" data-id="element-2443">
        <div className="flex items-center justify-between mb-1.5" data-id="element-2444">
          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-md ${masteryInfo.bg} ${masteryInfo.color}`} data-id="element-2445">
            {masteryInfo.label}
          </span>
          {attempts > 0 && <span className="text-[10px] font-bold text-slate-400" data-id="element-2446">
              {attempts}×
            </span>}
        </div>
        <div className="w-full h-1.5 bg-slate-100 rounded-full overflow-hidden" data-id="element-2447">
          <motion.div className="h-full rounded-full" style={{
          backgroundColor: masteryInfo.ring
        }} initial={{
          width: 0
        }} animate={{
          width: `${Math.min(100, Math.max(0, masteryScore))}%`
        }} transition={{
          duration: 0.8,
          ease: 'easeOut'
        }} data-id="element-2448" />
        </div>
      </div>
    </motion.button>;
}
function DjayPickCard({
  item,
  onSelect,
  accentColor,
  isQuickStart
}: {
  item: FamilyWithMastery;
  onSelect: (familyId: string) => void;
  accentColor: string;
  isQuickStart: boolean;
}) {
  const {
    family,
    masteryScore,
    attempts
  } = item;
  const module = MODULES.find(m => m.id === family.module);
  const ModIcon = getModuleIcon(module?.icon || '');
  const colors = module ? getModuleColors(module.color) : null;
  const masteryInfo = getMasteryLabelInfo(masteryScore);
  return <motion.button initial={{
    opacity: 0,
    y: 12
  }} animate={{
    opacity: 1,
    y: 0
  }} onClick={() => onSelect(family.familyId)} className="w-full text-left mb-4 p-4 rounded-3xl border-3 border-amber-300 bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 hover:shadow-xl active:scale-[0.99] transition-all group relative overflow-hidden" data-id="element-2449">
      <div className="absolute top-0 right-0 w-32 h-32 bg-amber-200/30 rounded-full blur-2xl -translate-y-1/2 translate-x-1/4 pointer-events-none" data-id="element-2450" />

      <div className="flex items-center gap-1.5 mb-3" data-id="element-2451">
        <div className="bg-amber-200/60 text-amber-600 p-1 rounded-lg" data-id="element-2452">
          <Sparkles className="w-3.5 h-3.5" data-id="element-2453" />
        </div>
        <span className="text-[11px] font-black text-amber-600 uppercase tracking-wider" data-id="element-2454">
          {isQuickStart ? "Djay's keuze — Snel starten!" : "Djay's keuze voor dit vak"}
        </span>
      </div>

      <div className="flex items-center gap-3 relative z-10" data-id="element-2455">
        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 bg-white shadow-sm ${colors?.text || 'text-slate-500'}`} data-id="element-2456">
          <ModIcon className="w-6 h-6" data-id="element-2457" />
        </div>
        <div className="flex-1 min-w-0" data-id="element-2458">
          <h4 className="text-base font-black text-slate-900 leading-tight line-clamp-2" data-id="element-2459">
            {family.description}
          </h4>
          <div className="flex items-center gap-2 mt-1" data-id="element-2460">
            <Badge variant="warning" size="sm" data-id="element-2461">
              {family.levelCode}
            </Badge>
            <span className="text-xs text-slate-500 font-medium truncate" data-id="element-2462">
              {module?.title}
            </span>
          </div>
        </div>
        <div className="flex-shrink-0 flex items-center gap-2" data-id="element-2463">
          <MasteryRing score={masteryScore} size={44} data-id="element-2464" />
          <ChevronRight className="w-5 h-5 text-amber-400 group-hover:translate-x-1 transition-transform" data-id="element-2465" />
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3" data-id="element-2466">
        <div className="flex-1" data-id="element-2467">
          <div className="flex items-center justify-between mb-1.5" data-id="element-2468">
            <span className={`text-xs font-bold ${masteryInfo.color}`} data-id="element-2469">
              {masteryInfo.label}
            </span>
            <span className="text-[10px] font-bold text-slate-400" data-id="element-2470">
              {attempts}× geoefend
            </span>
          </div>
          <div className="w-full h-2 bg-white/80 rounded-full overflow-hidden" data-id="element-2471">
            <motion.div className="h-full rounded-full bg-amber-400" initial={{
            width: 0
          }} animate={{
            width: `${Math.min(100, Math.max(0, masteryScore))}%`
          }} transition={{
            duration: 1,
            ease: 'easeOut'
          }} data-id="element-2472" />
          </div>
        </div>
      </div>
    </motion.button>;
}